
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Check, X, Clock, User } from 'lucide-react';
import { ApprovalRequest } from '@/hooks/useContentModeration';
import { toast } from 'sonner';

interface ApprovalRequestsTabProps {
  requests: ApprovalRequest[];
  onUpdateRequest: (id: string, status: 'approved' | 'denied', adminNotes?: string) => Promise<void>;
}

const ApprovalRequestsTab = ({ requests, onUpdateRequest }: ApprovalRequestsTabProps) => {
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);

  const handleAction = async (requestId: string, action: 'approved' | 'denied') => {
    setProcessing(requestId);
    try {
      await onUpdateRequest(requestId, action, adminNotes);
      toast.success(`Request ${action} successfully`);
      setSelectedRequest(null);
      setAdminNotes('');
    } catch (error) {
      toast.error(`Failed to ${action} request`);
    } finally {
      setProcessing(null);
    }
  };

  const pendingRequests = requests.filter(req => req.status === 'pending');
  const processedRequests = requests.filter(req => req.status !== 'pending');

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      denied: 'bg-red-100 text-red-800 border-red-200'
    };
    return variants[status as keyof typeof variants] || variants.pending;
  };

  return (
    <div className="space-y-6">
      {/* Pending Requests */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-500" />
            Pending Approval Requests ({pendingRequests.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {pendingRequests.length === 0 ? (
            <p className="text-gray-400">No pending approval requests</p>
          ) : (
            pendingRequests.map((request) => (
              <div key={request.id} className="bg-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-white font-medium">User ID: {request.user_id}</span>
                      <Badge className={getStatusBadge(request.status)}>
                        {request.status}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-300">
                      <p><strong>Requested Role:</strong> {request.requested_role}</p>
                      <p><strong>Reason:</strong> {request.reason}</p>
                      <p><strong>Requested:</strong> {new Date(request.requested_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {selectedRequest === request.id && (
                  <div className="space-y-3 border-t border-gray-600 pt-3">
                    <Textarea
                      placeholder="Add admin notes (optional)"
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      className="bg-gray-600 border-gray-500 text-white"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAction(request.id, 'approved')}
                        disabled={processing === request.id}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleAction(request.id, 'denied')}
                        disabled={processing === request.id}
                        variant="destructive"
                        size="sm"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Deny
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedRequest(null);
                          setAdminNotes('');
                        }}
                        variant="outline"
                        size="sm"
                        className="border-gray-600 text-gray-300"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {selectedRequest !== request.id && (
                  <Button
                    onClick={() => setSelectedRequest(request.id)}
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300 hover:bg-gray-600"
                  >
                    Review Request
                  </Button>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Recent Processed Requests */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Recent Processed Requests</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {processedRequests.slice(0, 10).map((request) => (
            <div key={request.id} className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm">User: {request.user_id}</span>
                    <Badge className={getStatusBadge(request.status)}>
                      {request.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-400">
                    Role: {request.requested_role} • {new Date(request.requested_at).toLocaleDateString()}
                  </p>
                  {request.admin_notes && (
                    <p className="text-xs text-gray-300">Notes: {request.admin_notes}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default ApprovalRequestsTab;
